import { DATASET_SHAPES, validateDataset } from '../datasets/datasetTypes';
import { ANALYTICS_SIZE_OPTIONS } from './visualDataModel';
import { ANALYTICS_GRAPH_TYPES } from './graphCatalog';
import FunnelVisual from './FunnelVisual';
import PipelineFlowVisual from './PipelineFlowVisual';
import MatrixVisual from './MatrixVisual';
import GeoTrafficVisual from './GeoTrafficVisual';
import TimeSeriesVisual from './TimeSeriesVisual';
import KpiVisual from './KpiVisual';
import ComparisonVisual from './ComparisonVisual';
import DistributionVisual from './DistributionVisual';
import GoalProgressVisual from './GoalProgressVisual';
import RadarVisual from './RadarVisual';
import RecruiterCardsVisual from './RecruiterCardsVisual';
import ForecastTimelineVisual from './ForecastTimelineVisual';
import StallingOffersVisual from './StallingOffersVisual';
import RiskActionTableVisual from './RiskActionTableVisual';
import SegmentedBarsVisual from './SegmentedBarsVisual';
import RetentionTimelineVisual from './RetentionTimelineVisual';

const renderer = (component, datasetShapes) => ({ component, datasetShapes });

const createRegistryEntry = ({
  renderers,
  sizeSupport = ANALYTICS_SIZE_OPTIONS
}) => ({
  renderers,
  sizeSupport
});

export const VISUAL_REGISTRY = {
  [ANALYTICS_GRAPH_TYPES.FUNNEL_WATERFALL]: createRegistryEntry({
    renderers: [renderer(FunnelVisual, [DATASET_SHAPES.STAGE_FLOW])]
  }),
  [ANALYTICS_GRAPH_TYPES.PIPELINE_FLOW]: createRegistryEntry({
    renderers: [renderer(PipelineFlowVisual, [DATASET_SHAPES.STAGE_FLOW])]
  }),
  [ANALYTICS_GRAPH_TYPES.FUNNEL]: createRegistryEntry({
    renderers: [renderer(FunnelVisual, [DATASET_SHAPES.STAGE_FLOW])]
  }),
  [ANALYTICS_GRAPH_TYPES.HORIZONTAL_BAR]: createRegistryEntry({
    renderers: [
      renderer(FunnelVisual, [DATASET_SHAPES.STAGE_FLOW]),
      renderer(ComparisonVisual, [DATASET_SHAPES.CATEGORICAL_COMPARISON, DATASET_SHAPES.GOAL_PROGRESS])
    ]
  }),
  [ANALYTICS_GRAPH_TYPES.SANKEY_FLOW]: createRegistryEntry({
    renderers: [renderer(FunnelVisual, [DATASET_SHAPES.STAGE_FLOW])]
  }),
  [ANALYTICS_GRAPH_TYPES.LINE_OVER_TIME]: createRegistryEntry({
    renderers: [renderer(TimeSeriesVisual, [DATASET_SHAPES.TIME_SERIES])]
  }),
  [ANALYTICS_GRAPH_TYPES.AREA_CHART]: createRegistryEntry({
    renderers: [renderer(TimeSeriesVisual, [DATASET_SHAPES.TIME_SERIES])]
  }),
  [ANALYTICS_GRAPH_TYPES.MULTI_LINE]: createRegistryEntry({
    renderers: [renderer(TimeSeriesVisual, [DATASET_SHAPES.TIME_SERIES])]
  }),
  [ANALYTICS_GRAPH_TYPES.BAR_OVER_TIME]: createRegistryEntry({
    renderers: [renderer(TimeSeriesVisual, [DATASET_SHAPES.TIME_SERIES])]
  }),
  [ANALYTICS_GRAPH_TYPES.STACKED_AREA]: createRegistryEntry({
    renderers: [renderer(TimeSeriesVisual, [DATASET_SHAPES.TIME_SERIES])]
  }),
  [ANALYTICS_GRAPH_TYPES.GROUPED_BAR]: createRegistryEntry({
    renderers: [renderer(ComparisonVisual, [DATASET_SHAPES.CATEGORICAL_COMPARISON, DATASET_SHAPES.GOAL_PROGRESS])]
  }),
  [ANALYTICS_GRAPH_TYPES.STACKED_BAR]: createRegistryEntry({
    renderers: [renderer(ComparisonVisual, [DATASET_SHAPES.CATEGORICAL_COMPARISON])]
  }),
  [ANALYTICS_GRAPH_TYPES.STACKED_HORIZONTAL_BAR]: createRegistryEntry({
    renderers: [renderer(ComparisonVisual, [DATASET_SHAPES.CATEGORICAL_COMPARISON])]
  }),
  [ANALYTICS_GRAPH_TYPES.SCATTER]: createRegistryEntry({
    renderers: [
      renderer(ComparisonVisual, [DATASET_SHAPES.CATEGORICAL_COMPARISON, DATASET_SHAPES.GOAL_PROGRESS]),
      renderer(DistributionVisual, [DATASET_SHAPES.DISTRIBUTION])
    ]
  }),
  [ANALYTICS_GRAPH_TYPES.BUBBLE_CHART]: createRegistryEntry({
    renderers: [renderer(ComparisonVisual, [DATASET_SHAPES.CATEGORICAL_COMPARISON])]
  }),
  [ANALYTICS_GRAPH_TYPES.RADAR]: createRegistryEntry({
    renderers: [renderer(RadarVisual, [DATASET_SHAPES.RADAR])]
  }),
  [ANALYTICS_GRAPH_TYPES.HEATMAP]: createRegistryEntry({
    renderers: [renderer(MatrixVisual, [DATASET_SHAPES.MATRIX])]
  }),
  [ANALYTICS_GRAPH_TYPES.TABLE]: createRegistryEntry({
    renderers: [
      renderer(MatrixVisual, [DATASET_SHAPES.MATRIX]),
      renderer(ComparisonVisual, [DATASET_SHAPES.CATEGORICAL_COMPARISON, DATASET_SHAPES.GOAL_PROGRESS])
    ]
  }),
  [ANALYTICS_GRAPH_TYPES.CONDITIONAL_TABLE]: createRegistryEntry({
    renderers: [renderer(MatrixVisual, [DATASET_SHAPES.MATRIX])]
  }),
  [ANALYTICS_GRAPH_TYPES.KPI_CARD]: createRegistryEntry({
    renderers: [renderer(KpiVisual, [DATASET_SHAPES.KPI])]
  }),
  [ANALYTICS_GRAPH_TYPES.OFFER_SIGNAL_CARD]: createRegistryEntry({
    renderers: [renderer(KpiVisual, [DATASET_SHAPES.KPI])]
  }),
  [ANALYTICS_GRAPH_TYPES.SOURCE_RANKINGS]: createRegistryEntry({
    renderers: [renderer(ComparisonVisual, [DATASET_SHAPES.CATEGORICAL_COMPARISON])]
  }),
  [ANALYTICS_GRAPH_TYPES.RECRUITER_CARDS]: createRegistryEntry({
    renderers: [renderer(RecruiterCardsVisual, [DATASET_SHAPES.CATEGORICAL_COMPARISON])]
  }),
  [ANALYTICS_GRAPH_TYPES.KPI_SPARKLINE]: createRegistryEntry({
    renderers: [renderer(KpiVisual, [DATASET_SHAPES.KPI])]
  }),
  [ANALYTICS_GRAPH_TYPES.GAUGE]: createRegistryEntry({
    renderers: [renderer(KpiVisual, [DATASET_SHAPES.KPI])]
  }),
  [ANALYTICS_GRAPH_TYPES.GOAL_TRACK]: createRegistryEntry({
    renderers: [renderer(GoalProgressVisual, [DATASET_SHAPES.GOAL_PROGRESS])]
  }),
  [ANALYTICS_GRAPH_TYPES.GOAL_LABEL_TREND]: createRegistryEntry({
    renderers: [renderer(GoalProgressVisual, [DATASET_SHAPES.GOAL_PROGRESS])]
  }),
  [ANALYTICS_GRAPH_TYPES.RADIAL_RINGS]: createRegistryEntry({
    renderers: [
      renderer(GoalProgressVisual, [DATASET_SHAPES.GOAL_PROGRESS]),
      renderer(ComparisonVisual, [DATASET_SHAPES.CATEGORICAL_COMPARISON])
    ]
  }),
  [ANALYTICS_GRAPH_TYPES.PROGRESS_BARS]: createRegistryEntry({
    renderers: [renderer(ComparisonVisual, [DATASET_SHAPES.GOAL_PROGRESS, DATASET_SHAPES.CATEGORICAL_COMPARISON])]
  }),
  [ANALYTICS_GRAPH_TYPES.BULLET_CHART]: createRegistryEntry({
    renderers: [renderer(ComparisonVisual, [DATASET_SHAPES.GOAL_PROGRESS, DATASET_SHAPES.CATEGORICAL_COMPARISON])]
  }),
  [ANALYTICS_GRAPH_TYPES.BOX_PLOT]: createRegistryEntry({
    renderers: [renderer(DistributionVisual, [DATASET_SHAPES.DISTRIBUTION])]
  }),
  [ANALYTICS_GRAPH_TYPES.VIOLIN_PLOT]: createRegistryEntry({
    renderers: [renderer(DistributionVisual, [DATASET_SHAPES.DISTRIBUTION])]
  }),
  [ANALYTICS_GRAPH_TYPES.HISTOGRAM]: createRegistryEntry({
    renderers: [renderer(DistributionVisual, [DATASET_SHAPES.DISTRIBUTION])]
  }),
  [ANALYTICS_GRAPH_TYPES.WORLD_CHOROPLETH]: createRegistryEntry({
    renderers: [renderer(GeoTrafficVisual, [DATASET_SHAPES.GEO])]
  }),
  [ANALYTICS_GRAPH_TYPES.REGIONAL_MAP]: createRegistryEntry({
    renderers: [renderer(GeoTrafficVisual, [DATASET_SHAPES.GEO])]
  }),
  [ANALYTICS_GRAPH_TYPES.CITY_DOT_MAP]: createRegistryEntry({
    renderers: [renderer(GeoTrafficVisual, [DATASET_SHAPES.GEO])]
  }),
  [ANALYTICS_GRAPH_TYPES.GANTT_TIMELINE]: createRegistryEntry({
    renderers: [renderer(ForecastTimelineVisual, [DATASET_SHAPES.TIMELINE])]
  }),
  [ANALYTICS_GRAPH_TYPES.STALLING_OFFERS]: createRegistryEntry({
    renderers: [renderer(StallingOffersVisual, [DATASET_SHAPES.STATUS_SEGMENTS])]
  }),
  [ANALYTICS_GRAPH_TYPES.RISK_ACTION_TABLE]: createRegistryEntry({
    renderers: [renderer(RiskActionTableVisual, [DATASET_SHAPES.ACTION_TABLE])]
  }),
  [ANALYTICS_GRAPH_TYPES.STACKED_BAR_LINE]: createRegistryEntry({
    renderers: [renderer(SegmentedBarsVisual, [DATASET_SHAPES.SEGMENTED_BARS])]
  }),
  [ANALYTICS_GRAPH_TYPES.RETENTION_HEATMAP]: createRegistryEntry({
    renderers: [renderer(RetentionTimelineVisual, [DATASET_SHAPES.MATRIX])]
  })
};

export const resolveVisualRenderer = (graphId, datasetShape) => {
  const entry = VISUAL_REGISTRY[graphId];
  if (!entry) return null;
  return entry.renderers.find((candidate) => candidate.datasetShapes.includes(datasetShape))?.component || null;
};

export const getCompatibleGraphs = (libraryEntry, bundle, size = 'medium') =>
  ((Array.isArray(bundle?.supportedGraphs) && bundle.supportedGraphs.length)
    ? bundle.supportedGraphs
    : (libraryEntry?.supportedGraphs || [])
  ).filter((graphId) => {
    const dataset = bundle?.datasets?.[graphId];
    const entry = VISUAL_REGISTRY[graphId];
    if (!dataset || !entry || !entry.sizeSupport.includes(size)) return false;
    if (!resolveVisualRenderer(graphId, dataset.shape)) return false;
    return validateDataset(dataset).valid;
  });
